import { useId, useState } from 'react';
import {
  SELECTABLE_REPLY_TARGETS,
  isAllWorkersSelection,
  type MainCommandReplyIdentity,
  type MainCommandReplyTarget,
} from '../lib/main-command-recipients';
import type { WorkerChatReadiness } from '../lib/worker-chat-readiness';
import { overseerAiProfile, type AiExecutionProfileView } from '../lib/ai-execution-profile';
import { AiExecutionProfileStrip, type AiExecutionProfileApplyResult, type AiExecutionProfileChange } from './AiExecutionProfileStrip';
import { DisclosureChevron } from './DisclosureChevron';
import './ReplyTargetBar.css';

function workerAvailabilityClass(worker: WorkerChatReadiness): 'available' | 'waiting' | 'unavailable' {
  if (worker.availability === 'available') return 'available';
  if (worker.availability === 'busy' || worker.availability === 'waiting'
    || worker.availability === 'cannot_interleave') return 'waiting';
  return 'unavailable';
}

export function ReplyTargetBar({
  selected,
  identities,
  onToggle,
  workers = [],
  eligibleWorkerIds,
  selectedWorkers,
  onToggleWorker,
  onToggleAllWorkers,
  overseerProfile,
  onApplyOverseerProfile,
  defaultExpanded = false,
}: {
  readonly selected: ReadonlySet<MainCommandReplyTarget>;
  readonly identities: Readonly<Record<MainCommandReplyTarget, MainCommandReplyIdentity>>;
  readonly onToggle: (target: MainCommandReplyTarget) => void;
  readonly workers?: readonly WorkerChatReadiness[];
  readonly eligibleWorkerIds?: ReadonlySet<string>;
  readonly selectedWorkers?: ReadonlySet<string>;
  readonly onToggleWorker?: (workerId: string) => void;
  /** Adds every eligible Worker, or removes them all when they are already selected. */
  readonly onToggleAllWorkers?: () => void;
  readonly overseerProfile?: AiExecutionProfileView;
  readonly onApplyOverseerProfile?: (change: AiExecutionProfileChange) => Promise<AiExecutionProfileApplyResult>;
  /** Default stays compact; the override is useful for an explicitly expanded host or static proof. */
  readonly defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const detailId = useId();
  const workerSelection = selectedWorkers ?? new Set<string>();
  const eligible = eligibleWorkerIds ?? new Set(workers.map((worker) => worker.workerId));
  const selectedWorkerIds = [...workerSelection];
  const selectedWorkerRows = selectedWorkerIds.map((workerId) => workers.find((worker) => worker.workerId === workerId) ?? null);
  const allWorkersSelected = isAllWorkersSelection(workerSelection, eligible);
  // A Worker picked individually (an @mention) stays in the same row unless All workers already covers it.
  const individualWorkerIds = selectedWorkerIds.filter((workerId) => !(allWorkersSelected && eligible.has(workerId)));

  return (
    <section
      className={expanded ? 'reply-target-bar is-expanded' : 'reply-target-bar'}
      aria-label="Reply to selected participants"
      data-testid="main-command-reply-targets"
      data-expanded={expanded ? 'true' : 'false'}
    >
      <div className="reply-target-heading">
        <span className="reply-target-label">Reply to selected participants</span>
        <button
          type="button"
          className="ghost icon reply-target-disclosure"
          aria-label={expanded ? 'Hide reply details' : 'Show reply details'}
          title={expanded ? 'Hide reply details' : 'Show reply details'}
          aria-expanded={expanded}
          aria-controls={detailId}
          onClick={() => setExpanded((open) => !open)}
        >
          <DisclosureChevron direction={expanded ? 'up' : 'down'} />
        </button>
      </div>

      <div className="reply-target-options" role="group" aria-label="Reply targets" data-participant-group="reply-targets">
        {SELECTABLE_REPLY_TARGETS.map((target) => {
          const identity = identities[target];
          const on = selected.has(target);
          return (
            <button
              key={target}
              type="button"
              className={on ? `reply-target-chip reply-target-${target} selected` : `reply-target-chip reply-target-${target}`}
              aria-pressed={on}
              aria-label={`${identity.roleLabel}: ${identity.provider}, ${identity.runtime}, ${identity.model}, Thinking ${identity.thinking}, ${identity.node}, ${identity.sourceLabel}, ${identity.availabilityLabel}`}
              onClick={() => onToggle(target)}
            >
              <span className="reply-target-selection" aria-hidden="true">{on ? '✓' : ''}</span>
              <span className="reply-target-role">{identity.roleLabel}</span>
              <span
                className={identity.available ? 'reply-target-dot available' : 'reply-target-dot unavailable'}
                aria-hidden="true"
              />
            </button>
          );
        })}
        <button
          type="button"
          className={allWorkersSelected ? 'reply-target-chip reply-target-worker reply-target-all-workers selected' : 'reply-target-chip reply-target-worker reply-target-all-workers'}
          aria-pressed={allWorkersSelected}
          aria-label={`All workers: ${eligible.size} currently eligible`}
          title="All currently eligible Workers"
          disabled={eligible.size === 0}
          data-reply-target="all-workers"
          onClick={() => onToggleAllWorkers?.()}
        >
          <span className="reply-target-selection" aria-hidden="true">{allWorkersSelected ? '✓' : ''}</span>
          <span className="reply-target-role">All workers</span>
        </button>
        {individualWorkerIds.map((workerId) => {
          const worker = workers.find((row) => row.workerId === workerId) ?? null;
          if (worker === null) return (
            <button key={workerId} type="button" className="reply-target-chip reply-target-worker selected"
              aria-pressed="true" data-worker-id={workerId} aria-label={`Selected Worker ${workerId}. Availability Unknown`}
              onClick={() => onToggleWorker?.(workerId)}>
              <span className="reply-target-selection" aria-hidden="true">✓</span>
              <span className="reply-target-role">Worker</span>
              <span className="reply-target-dot unavailable" aria-hidden="true" />
            </button>
          );
          const stateClass = workerAvailabilityClass(worker);
          return (
            <button
              key={worker.workerId}
              type="button"
              className="reply-target-chip reply-target-worker selected"
              aria-pressed="true"
              data-worker-id={workerId}
              data-worker-availability={worker.availability}
              aria-label={`${worker.label}: ${worker.providerLabel ?? 'Unknown provider'}, ${worker.runtimeLabel ?? worker.runtimeKey ?? 'Unknown runtime'}, ${worker.modelKey ?? 'Unknown model'}, ${worker.nodeName ?? 'Unknown machine'}, ${worker.availability}. ${worker.reasonText}`}
              onClick={() => onToggleWorker?.(workerId)}
            >
              <span className="reply-target-selection" aria-hidden="true">✓</span>
              <span className="reply-target-role">{worker.label}</span>
              <span className={`reply-target-dot ${stateClass}`} aria-hidden="true" />
            </button>
          );
        })}
      </div>

      {expanded && (
        <div id={detailId} className="reply-target-expanded" data-testid="main-command-reply-details">
          <div className="reply-target-details">
            {SELECTABLE_REPLY_TARGETS.map((target) => {
              const identity = identities[target];
              return (
                <article key={target} className={`reply-target-detail reply-target-${target}`} data-participant-role={target}>
                  <strong>{identity.roleLabel}</strong>
                  <span>{selected.has(target) ? 'Selected' : 'Not selected'}</span>
                  <small>{identity.provider} · {identity.runtime} · {identity.model}</small>
                  <small>Thinking: {identity.thinking} · {identity.node}</small>
                  <small className={identity.available ? 'available' : 'unavailable'}>
                    {identity.sourceLabel} · {identity.availabilityLabel}
                  </small>
                </article>
              );
            })}
            {selectedWorkerRows.map((worker) => {
              if (worker === null) return null;
              const stateClass = workerAvailabilityClass(worker);
              return (
                <article
                  key={worker.workerId}
                  className="reply-target-detail reply-target-worker"
                  data-participant-role="worker"
                  data-worker-id={worker.workerId}
                >
                  <strong>{worker.label}</strong>
                  <span>{workerSelection.has(worker.workerId) ? 'Selected' : 'Not selected'}</span>
                  <small>{worker.providerLabel ?? 'Unknown provider'} · {worker.runtimeLabel ?? worker.runtimeKey ?? 'Unknown runtime'} · {worker.modelKey ?? 'Unknown model'}</small>
                  <small>{worker.nodeName ?? 'Unknown machine'} · {worker.availability.replaceAll('_', ' ')}</small>
                  <small className={stateClass}>{worker.reasonText}</small>
                </article>
              );
            })}
          </div>
          <AiExecutionProfileStrip profile={overseerProfile ?? overseerAiProfile(identities.overseer)}
            onApply={onApplyOverseerProfile} compact />
        </div>
      )}
    </section>
  );
}
